import * as React from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FieldMessages } from './field-messages';
import { fieldDescribedBy, labelStyles } from './form-field-styles';
import { SelectListboxPopover } from './select-listbox-popover';
import type { SelectProps } from './select-props';
import { selectTriggerClasses } from './select-trigger-classes';
import { useSelectListState } from './use-select-list-state';

export type { SelectOption, SelectGroup, SelectMatchMode } from './select-option-utils';
export type { SelectProps } from './select-props';

/**
 * Select component: a button trigger with a portalled listbox, optional search,
 * and full keyboard support (see `use-select-keyboard-navigation.ts`).
 */
const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = 'Select...',
      label,
      helperText,
      error,
      searchable = false,
      searchPlaceholder,
      matchMode = 'startsWith',
      disabled = false,
      loading = false,
      name,
      className,
      containerClassName,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-invalid': ariaInvalid = false,
      'aria-busy': ariaBusy = false,
      autoFocus,
      variant = 'default',
      active = false,
      triggerIcon: TriggerIcon = ChevronDown,
    },
    ref
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const popoverRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);

    // Merge forwarded ref with internal trigger ref (needed for positioning and refocus)
    const setTriggerRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        triggerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      },
      [ref]
    );

    const isDisabled = disabled || loading;
    const generatedId = React.useId();
    const selectId = id || generatedId;
    const listboxId = `${selectId}-listbox`;
    const labelId = `${selectId}-label`;
    const valueId = `${selectId}-value`;
    const hasError = !!error || ariaInvalid;

    const list = useSelectListState({
      selectId,
      options,
      value,
      onChange,
      searchable,
      matchMode,
      isDisabled,
      growToContent: variant === 'inline',
      containerRef,
      popoverRef,
      triggerRef,
      searchInputRef,
    });
    const { isOpen } = list;

    const selectedOption = list.allOptions.find((opt) => opt.value === value);
    const displayLabel = loading ? 'Loading…' : selectedOption?.label || placeholder;
    const showsPlaceholder = !loading && !selectedOption;
    // The search box is named for what it searches unless the caller gave its own text.
    const searchName = searchPlaceholder ?? (label || ariaLabel ? `Search ${label || ariaLabel}` : 'Search');
    // Named only by an outside label: "Search" + that label, by reference (its text isn't known here).
    const searchWordId = `${selectId}-search-word`;
    const searchLabelledBy =
      searchable && !searchPlaceholder && !label && !ariaLabel && ariaLabelledBy ? `${searchWordId} ${ariaLabelledBy}` : undefined;
    // Accessible name = label (or an outside label, or the trigger's own aria-label) + the current value.
    const nameId = label ? labelId : ariaLabelledBy || (ariaLabel ? selectId : undefined);
    const triggerLabelledBy = nameId ? `${nameId} ${valueId}` : undefined;

    return (
      <div
        ref={containerRef}
        className={cn('relative', variant === 'inline' ? 'inline-block max-w-full' : 'w-full', containerClassName)}
      >
        {label && (
          <label id={labelId} htmlFor={selectId} className={labelStyles}>
            {label}
          </label>
        )}

        <button
          ref={setTriggerRef}
          id={selectId}
          type="button"
          // Searchable: the search box is the combobox while open, so the trigger is a plain popup button.
          role={searchable ? undefined : 'combobox'}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={searchable ? undefined : list.activeDescendant}
          aria-label={ariaLabel}
          aria-labelledby={triggerLabelledBy}
          aria-invalid={hasError}
          aria-busy={loading || ariaBusy || undefined}
          // Loading uses aria-disabled, not `disabled`, so a focused trigger keeps focus while it loads.
          aria-disabled={loading || undefined}
          aria-describedby={fieldDescribedBy(selectId, { error, helperText, extra: ariaDescribedBy })}
          disabled={disabled}
          autoFocus={autoFocus}
          onClick={() => (isOpen ? list.closeMenu(true) : list.openMenu())}
          onKeyDown={(event) => {
            if (!isDisabled) list.onTriggerKeyDown(event);
          }}
          className={cn(selectTriggerClasses({ variant, isOpen, hasError, isDisabled, active }), className)}
        >
          <span
            id={valueId}
            className={cn('flex min-w-0 items-center gap-2 truncate', showsPlaceholder && 'text-clovie-muted')}
          >
            {loading && <Loader2 aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin" />}
            <span className="truncate">{displayLabel}</span>
          </span>
          <span aria-hidden="true" className="contents">
            <TriggerIcon
              className={cn(
                'h-[18px] w-[18px] shrink-0 text-clovie-muted transition-transform duration-200',
                isOpen && TriggerIcon === ChevronDown && 'rotate-180'
              )}
            />
          </span>
        </button>

        {name !== undefined && <input type="hidden" name={name} value={value ?? ''} />}
        {searchLabelledBy && (
          <span id={searchWordId} hidden>
            Search
          </span>
        )}

        {isOpen && list.position && (
          <SelectListboxPopover
            popoverRef={popoverRef}
            listboxId={listboxId}
            // Named like the trigger, without the value; the trigger itself (aria-label or text) as a last resort.
            labelledBy={nameId ?? selectId}
            position={list.position}
            options={list.filteredOptions}
            value={value}
            activeValue={list.activeValue}
            optionId={list.optionId}
            onActivate={(next) => list.setActiveValue(next)}
            onPick={list.selectValue}
            search={
              searchable
                ? {
                    inputRef: searchInputRef,
                    query: list.searchQuery,
                    placeholder: searchPlaceholder ?? 'Search ...',
                    ariaLabel: searchName,
                    labelledBy: searchLabelledBy,
                    onQueryChange: list.setQuery,
                    onKeyDown: list.onSearchKeyDown,
                    activeDescendant: list.activeDescendant,
                  }
                : undefined
            }
          />
        )}

        <FieldMessages fieldId={selectId} error={error} helperText={helperText} />
      </div>
    );
  }
);
Select.displayName = 'Select';

export { Select };
